import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { Box, CircularProgress, Tooltip } from "@mui/material";
import React, { useLayoutEffect, useRef } from "react";

// Pipeline task keys from the projects endpoint, in the order it sends them.
const TASK_LABELS = Object.freeze({
    demucs: "Stems",
    transcribe: "Transcribe",
    nonverbalDetection: "Non-verbals",
    translation: "Translate",
    audioChopping: "Chop audio",
    voiceTraining: "Train voices",
    voiceGeneration: "Create takes",
    lipSync: "Lip sync",
    merging: "Remux",
});

const LINE_HEIGHT = 24;
// The table cell holding the list: it keeps at least two lines in view and
// the list fills it edge to edge, past the cell's padding.
export const PROGRESS_CELL_SX = Object.freeze({
    position: "relative",
    boxSizing: "border-box",
    width: 190,
    minWidth: 190,
    height: LINE_HEIGHT * 2,
});
const numberFormat = new Intl.NumberFormat();
const dateFormat = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });

const formatDate = (iso) => {
    const time = iso ? new Date(iso) : null;
    return time && !Number.isNaN(time.getTime()) ? dateFormat.format(time) : null;
};

// The line the box opens with at its bottom edge, so the latest finished task
// sits just above the task that comes after it: a failure first, so it is
// never scrolled out of sight, else the task after the latest finished one
// (or that task itself when it is the last). Null when nothing has finished,
// so the box opens at the start of the pipeline.
export const getBottomIndex = (pipeline) => {
    const failed = pipeline.findIndex((task) => task.status === "failed");
    if (failed !== -1) {
        return failed;
    }
    for (let index = pipeline.length - 1; index >= 0; index -= 1) {
        if (pipeline[index].status === "completed") {
            return Math.min(index + 1, pipeline.length - 1);
        }
    }
    return null;
};

const scrollToBottomLine = (box, bottomIndex) => {
    box.scrollTop =
        bottomIndex === null
            ? 0
            : Math.max(0, (bottomIndex + 1) * LINE_HEIGHT - box.clientHeight);
};

const StatusIcon = ({ status }) => {
    const sx = { fontSize: 16, flexShrink: 0 };
    if (status === "completed") {
        return <CheckCircleRoundedIcon sx={{ ...sx, color: "success.main" }} />;
    }
    if (status === "failed") {
        return <ErrorRoundedIcon sx={{ ...sx, color: "error.main" }} />;
    }
    if (status === "inProgress") {
        return (
            <Box sx={{ width: 16, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                <CircularProgress size={12} thickness={6} aria-hidden />
            </Box>
        );
    }
    return <RadioButtonUncheckedRoundedIcon sx={{ ...sx, color: "text.disabled" }} />;
};

const describe = (task) => {
    if (task.status === "inProgress") {
        return Number.isInteger(task.total)
            ? `${numberFormat.format(task.done)} / ${numberFormat.format(task.total)}`
            : "running";
    }
    if (task.status === "failed") {
        return "failed";
    }
    if (task.status === "completed") {
        return formatDate(task.finishedAt) || "done";
    }
    return "";
};

const tooltipFor = (task, label) => {
    if (task.status === "failed") {
        return `${label} failed: ${task.error || "Task failed"}`;
    }
    if (task.status === "completed" && task.finishedAt) {
        return `${label} finished ${new Date(task.finishedAt).toLocaleString()}`;
    }
    return "";
};

const TaskLine = ({ task }) => {
    const label = TASK_LABELS[task.key] || task.key;
    const detail = describe(task);
    const muted = task.status === "notStarted";
    return (
        <Tooltip title={tooltipFor(task, label)} placement="left" arrow>
            <Box
                role="listitem"
                data-status={task.status}
                sx={{
                    height: LINE_HEIGHT,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    fontSize: "0.8rem",
                    whiteSpace: "nowrap",
                    color: muted ? "text.disabled" : "var(--color-body-text)",
                    fontWeight: task.status === "inProgress" ? 600 : 400,
                }}
            >
                <StatusIcon status={task.status} />
                <Box component="span" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {label}
                </Box>
                <Box
                    component="span"
                    sx={{
                        fontVariantNumeric: "tabular-nums",
                        color: task.status === "failed" ? "error.main" : "text.secondary",
                    }}
                >
                    {detail}
                </Box>
            </Box>
        </Tooltip>
    );
};

const ProjectProgressCell = ({ pipeline }) => {
    const boxRef = useRef(null);
    const tasks = Array.isArray(pipeline) ? pipeline : [];
    const bottomIndex = getBottomIndex(tasks);

    useLayoutEffect(() => {
        const box = boxRef.current;
        if (!box) {
            return undefined;
        }
        scrollToBottomLine(box, bottomIndex);
        // The box takes the row's height, which can settle after first paint.
        if (typeof ResizeObserver === "undefined") {
            return undefined;
        }
        const observer = new ResizeObserver(() => scrollToBottomLine(box, bottomIndex));
        observer.observe(box);
        return () => observer.disconnect();
    }, [bottomIndex]);

    if (tasks.length === 0) {
        return <Box sx={{ color: "text.disabled", textAlign: "center" }}>—</Box>;
    }

    return (
        <Box
            ref={boxRef}
            role="list"
            aria-label="Pipeline progress"
            tabIndex={0}
            sx={{
                position: "absolute",
                inset: 0,
                overflowY: "auto",
                textAlign: "left",
                // Still scrolls by wheel, touch and keyboard; no bar takes space.
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
            }}
        >
            {tasks.map((task) => (
                <TaskLine key={task.key} task={task} />
            ))}
        </Box>
    );
};

export default ProjectProgressCell;
